import React, { useEffect, useState } from "react";
import "../../../styles/publicForm.css";
import useNoDocumentScroll from "../hooks/useNoDocumentScroll";
import type { PublicBrand } from "../../enquiries/types/enquiries.types";

/**
 * The three seconds a practitioner's form opens on: their mark on a white
 * screen, and nothing else at all.
 *
 * It is doing a job the welcome cannot do for itself. The link arrives in a
 * WhatsApp message with no explanation, and the first thing a stranger needs
 * to settle is whose page they have just landed on — before they are asked to
 * read a greeting, and long before they are asked for their phone number. A
 * logo alone, held long enough to be looked at, answers that in the only
 * language a mark speaks.
 *
 * It also covers the read. The form and the logo file are two fetches, and
 * without something in front of them the greeting lands first and the mark
 * drops in on top of it a moment later. Here the whole composition is assembled
 * behind a screen nobody is reading and arrives finished.
 *
 * Naurist is not on it, as Naurist is not on any of these screens. If the
 * practitioner uploaded no logo their name is set instead, and if they have
 * neither the three seconds are white — which is still their page, and still
 * better than our mark on it.
 */

/**
 * How long it is held.
 *
 * Three seconds is long by the standards of anything else in this app and is
 * the point: shorter reads as a flash of something the reader missed, and the
 * only thing on the screen is a logo they are meant to have taken in. It is
 * spent once, on the first screen of a single sitting, and never again.
 */
export const SPLASH_MS = 3000;

/**
 * Whether the splash is still up.
 *
 * Called by both form pages, at the top and unconditionally — it owns a timer,
 * and a hook that ran on some renders and not others would lose it.
 *
 * `nothingToWaitFor` ends it early, and it is the states with no form behind
 * them: a link that was never real, one whose form is already filled in, a
 * server that could not be reached. There is no brand to show on any of them
 * and no greeting coming afterwards, so the splash would be three seconds of
 * blank white in front of an apology. Once down it stays down — a reader who
 * presses Try again and gets through goes straight to the welcome rather than
 * back to a splash they have already sat through.
 */
export const useSplash = (nothingToWaitFor: boolean): boolean => {
  const [showing, setShowing] = useState(true);

  useEffect(() => {
    const timer = window.setTimeout(() => setShowing(false), SPLASH_MS);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (nothingToWaitFor) setShowing(false);
  }, [nothingToWaitFor]);

  return showing;
};

const PublicFormSplash: React.FC<{ brand: PublicBrand }> = ({ brand }) => {
  // One mark on a white screen, held still. Three seconds of a logo that can
  // be dragged up to show a strip of nothing under it is three seconds of a
  // page that looks unfinished — see `useNoDocumentScroll`.
  useNoDocumentScroll(true);

  // The same fallback the shell's lockup makes, for the same reason: a stored
  // logo is fetched from the API's origin and that fetch can fail. A broken
  // image icon alone on a white screen is the worst first impression this
  // page could give, so the name takes over.
  const [broken, setBroken] = useState(false);
  const url = brand.logoUrl;

  return (
    // Exactly the visible height and no scroll — see `form-page-fit`. There is
    // one element on this screen and no reason for it ever to move.
    <div className="form-page-fit flex w-full items-center justify-center bg-white px-10">
      {url && !broken ? (
        <img
          key={url}
          src={url}
          alt={brand.name || "Logo"}
          onError={() => setBroken(true)}
          // Bound on both axes rather than on height alone: a logo is any
          // aspect ratio, and the one shape that must not happen here is a
          // wide wordmark running to the edges of the glass. A third of the
          // screen is what a mark standing on its own wants.
          //
          // A share of the width is the right rule on a phone and the wrong
          // one on a desktop: 62% of a 1400px window is a logo blown up to
          // seven inches across, which reads as a broken image rather than as
          // a mark. From `sm:` up it is capped at a size a logo is actually
          // drawn at, and the window getting wider stops making it bigger.
          className="max-h-[30svh] w-auto max-w-[62%] sm:max-h-[22svh] sm:max-w-[240px] object-contain motion-safe:animate-splash-in"
        />
      ) : brand.name ? (
        <span className="text-[26px] font-semibold text-[#0A4A52] motion-safe:animate-splash-in">
          {brand.name}
        </span>
      ) : null}
    </div>
  );
};

export default PublicFormSplash;
